<template>
  <v-container>
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      id="prenatal-consent-section"
    >
      <v-row>
        <v-col class="custom-col">
          <h2>Patient's consent</h2>
        </v-col>
      </v-row>
      <UploadComponent
        v-if="this.$dataset.isUploadConsentEnabled"
        :testId="testId"
        :testName="testName"
        :updateOnDestroy="true"
        @updateUploadObj="updateUploadObj"
        uploadType="consent"
      />
      <v-row>
        <v-col class="custom-col">
          <h3>{{ testName }} test consent</h3>
        </v-col>
      </v-row>

      <CheckQuestionRow
        :isChecked="patientClinicalConsent"
        @updateAck="updatePatientClinicalConsent"
        testId="prenatal-patient-clinical-consent"
      >
        <p>
          The requesting clinician acknowledges that the patient/parent/guardian
          has provided consent for genomic testing as summarised in the genomics
          consent form and patient information available here:<br />
        </p>
        <p>The patient/parent/guardian has:</p>
        <ul>
          <li
            v-for="(txt,
            index) in $options.patientConsentPractitionerAssertions"
            :key="index"
          >
            {{ txt }}
          </li>
        </ul>
      </CheckQuestionRow>

      <v-row>
        <v-col class="custom-col">
          <p
            v-if="
              isPatientClinicalConsentDirty && !isPatientClinicalConsentValid
            "
            data-testid="prenatal-patient-clinical-consent-warning"
            class="red--text"
          >
            Patient consent must be acknowledged
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="custom-col">
          <h3>Research consent</h3>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="custom-col">
          <YesNo
            testId="prenatal-patient-research-consent"
            @updateAck="updatePatientResearchConsent"
          >
            <p>
              {{ $options.patientResearchConsentContent }}
            </p>
          </YesNo>
          <p
            v-if="
              isPatientResearchConsentDirty && !isPatientResearchConsentValid
            "
            class="red--text"
            data-testid="prenatal-patient-research-consent-warning"
          >
            A patient research consent option must be selected
          </p>
        </v-col>
      </v-row>

      <v-row v-if="isTrio">
        <v-col class="custom-col">
          <v-row>
            <v-col class="custom-col">
              <h2>Parental consent</h2>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <h3>
                Consent for biological parents undergoing trio sequencing
                analysis
              </h3>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <CheckQuestionRow
                :isChecked="parentClinicalConsentInterpretation"
                @updateAck="updateParentClinicalConsentInterpretation"
                testId="prenatal-parent-clinical-consent-interpretation"
              >
                {{ $options.parentClinicalConsentInterpretationContent }}
              </CheckQuestionRow>
              <CheckQuestionRow
                :isChecked="parentClinicalConsentIncidental"
                @updateAck="updateParentClinicalConsentIncidental"
                testId="prenatal-parent-clinical-consent-incidental"
              >
                {{ $options.parentClinicalConsentIncidentalContent }}
              </CheckQuestionRow>
              <p
                v-if="
                  isParentClinicalConsentDirty && !isParentClinicalConsentValid
                "
                class="red--text"
                data-testid="prenatal-parent-clinical-consent-warning"
              >
                Consent of parents to all aspects of genomic testing must be
                acknowledged
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <h3>Research consent (parent samples)</h3>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <p>
                {{ $options.parentResearchConsentContent }}
              </p>
              <YesNo
                @updateAck="updateMotherResearchConsent"
                testId="prenatal-mother-research-consent"
              >
                <p class="parent-consent">Biological mother</p>
              </YesNo>
              <YesNo
                @updateAck="updateFatherResearchConsent"
                testId="prenatal-father-research-consent"
              >
                <p class="parent-consent">Biological father</p>
              </YesNo>
              <p
                v-if="
                  isParentResearchConsentDirty && !isParentResearchConsentValid
                "
                class="red--text"
                data-testid="prenatal-parent-research-consent-warning"
              >
                A research consent option must be selected for both parents
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import FormRulesMixin from "../../../mixins/FormRulesMixin";
import YesNo from "../../common/YesNo";
import CheckQuestionRow from "@/components/common/CheckQuestionRow";
import UploadComponent from "../../upload/UploadComponent.vue";
import UploadMixin from "../../../mixins/UploadMixin.vue";

export default {
  props: {
    testName: String,
    testId: String,
    isTrio: Boolean,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
  },
  mixins: [FormRulesMixin, UploadMixin],
  name: "",
  components: {
    CheckQuestionRow,
    YesNo,
    UploadComponent,
  },

  /* static data accessible via $options
       NOTE: this content is split out from the template because these values are also passed out as part
       of the emitted result (for storage along side the data in the db - as a point in time reference to exactly
       what was consented to) */

  patientConsentPractitionerAssertions: [
    "Had enough time to consider the information in this consent form",
    "Had the opportunity to discuss genomic testing and its implications with a health professional",
    "Been given access to information about genomic testing",
    "Been able to ask questions until they were satisfied with the answers",
    "Been offered a copy of this consent form",
  ],
  patientResearchConsentContent:
    "The patient/parent/guardian has provided consent to share the sample, genomic data and related health " +
    "information of the patient for ethically approved research into the same or a related condition, " +
    "where it remains possible to re-identify the patient.  This allows relevant information to be " +
    "returned to the patient/parent/guardian where appropriate. There may not be a direct benefit to " +
    "the patient and their family.",
  parentClinicalConsentInterpretationContent:
    "The requesting clinician acknowledges that the biological parents consent to genomic testing for the " +
    "purpose of assisting in the interpretation of the genomic results of their child.",
  parentClinicalConsentIncidentalContent:
    "They understand the potential outcomes of the test including incidental findings and unexpected " +
    "family relationships, sharing and implications of the result, including insurance implications, " +
    "and that the sharing and storage of the sample, and genomic and health data, apply to the " +
    "collection and analysis performed on their own samples.",
  parentResearchConsentContent:
    "The biological parents have provided consent to share their samples, genomic data and related health " +
    "information for ethically approved research into their child’s condition, where it remains possible " +
    "to re-identify them. This allows information to be returned to parents where appropriate. There may " +
    "not be a direct benefit to parents or their family.",

  data: () => ({
    /* the pattern we use here is that these ack values are 3-way (boolean|null if we had Typescript)
         when null - it means the user has not interacted with them yet
         once non-null - they are dirty - and will then only have a boolean true or false */

    patientClinicalConsent: null,
    patientResearchConsent: null,

    parentClinicalConsentInterpretation: null,
    parentClinicalConsentIncidental: null,

    motherResearchConsent: null,
    fatherResearchConsent: null,

    // an overall master dirty state that is set when submit is attempted
  }),

  computed: {
    /* the *Valid() computations return whether the corresponding data is valid enough to proceed with Submission
         for some checkboxes this might mean they must be true - for others it might just mean they have a value */

    isPatientClinicalConsentValid() {
      // for patient consent they have to have actually said yes - no is not a valid answer
      return this.patientClinicalConsent === true;
    },
    isPatientResearchConsentValid() {
      // for research they can say Yes or No - as long as they say something
      return this.patientResearchConsent !== null;
    },
    isParentClinicalConsentValid() {
      // for parent clinical consent they have to have actually said yes - no is not a valid answer
      return (
        this.parentClinicalConsentInterpretation === true &&
        this.parentClinicalConsentIncidental === true
      );
    },
    isParentResearchConsentValid() {
      // for parent research they can say Yes or No - as long as they *both* say something
      return (
        this.motherResearchConsent !== null &&
        this.fatherResearchConsent != null
      );
    },

    /* the *Dirty() computations return whether the user has interacted in the UI with these fields - such
         that we can immediately start showing them validation messages */

    isPatientClinicalConsentDirty() {
      return this.patientClinicalConsent !== null || this.forceFormDirty;
    },
    isPatientResearchConsentDirty() {
      return this.patientResearchConsent !== null || this.forceFormDirty;
    },
    isParentClinicalConsentDirty() {
      return (
        this.parentClinicalConsentInterpretation !== null ||
        this.parentClinicalConsentIncidental !== null ||
        this.forceFormDirty
      );
    },
    isParentResearchConsentDirty() {
      return (
        this.motherResearchConsent !== null ||
        this.fatherResearchConsent !== null ||
        this.forceFormDirty
      );
    },
  },

  methods: {
    updatePatientClinicalConsent(ack) {
      this.patientClinicalConsent = ack;
    },
    updatePatientResearchConsent(ack) {
      this.patientResearchConsent = ack;
    },
    updateParentClinicalConsentInterpretation(ack) {
      this.parentClinicalConsentInterpretation = ack;
    },
    updateParentClinicalConsentIncidental(ack) {
      this.parentClinicalConsentIncidental = ack;
    },
    updateMotherResearchConsent(ack) {
      this.motherResearchConsent = ack;
    },
    updateFatherResearchConsent(ack) {
      this.fatherResearchConsent = ack;
    },
    isFormValid: function() {
      var isValid =
        this.isPatientClinicalConsentValid &&
        this.isPatientResearchConsentValid;

      if (this.isTrio) {
        isValid =
          isValid &&
          this.isParentClinicalConsentValid &&
          this.isParentResearchConsentValid;
      }
      return isValid;
    },
    transferData: function() {
      return {
        patientTestConsentAck: this.patientClinicalConsent,
        patientResearchConsentAck: this.patientResearchConsent,
        // could get away without doing this - but if they ask for the data _before_ the form is filled in we
        // make sure we don't needlessly return a big blob of text
        patientResearchConsentContent:
          this.patientResearchConsent !== null
            ? this.$options.patientResearchConsentContent
            : null,
        trioTestConsentAck: this.isTrio
          ? this.isParentClinicalConsentValid
          : null,
        motherResearchConsentAck: this.isTrio
          ? this.motherResearchConsent
          : null,
        fatherResearchConsentAck: this.isTrio
          ? this.fatherResearchConsent
          : null,
        // same again, we force these to be null until the question is answered as either Yes or No
        motherResearchConsentContent:
          this.isTrio && this.motherResearchConsent !== null
            ? this.$options.parentResearchConsentContent
            : null,
        fatherResearchConsentContent:
          this.isTrio && this.fatherResearchConsent !== null
            ? this.$options.parentResearchConsentContent
            : null,
      };
    },
  },
};
</script>

<style scoped>
.parent-consent {
  transform: translateY(2px) !important;
  margin-left: 4px !important;
}
</style>
